import React from "react";
import type { ReactElement } from "react";

import type { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import type { StackNavigationProp } from "@react-navigation/stack";

import type { HubTabNavigatorProps } from "pianofunclub-crm/navigation/HubNavigator";
import defaultNavOptions from "pianofunclub-crm/navigation/utils/defaultNavigationOptions";
import type {
  AccountParams,
  EnrolmentParams,
  InvoiceParams,
  SchoolParams,
  DefaultTermDatesParams,
} from "pianofunclub-crm/navigation/utils/screenParams";
import AccountScreen, {
  screenOptions as accountScreenOptions,
} from "pianofunclub-crm/screens/accounts/AccountScreen";
import AccountsHubScreen, {
  screenOptions as accountsHubScreenOptions,
} from "pianofunclub-crm/screens/accounts/AccountsHubScreen";
import InvoiceScreen, {
  screenOptions as invoiceScreenOptions,
} from "pianofunclub-crm/screens/invoicing/InvoiceScreen";
import DefaultTermDatesScreen, {
  screenOptions as defaultTermDatesScreenOptions,
} from "pianofunclub-crm/screens/schools/DefaultTermDatesScreen";
import SchoolScreen, {
  screenOptions as schoolScreenOptions,
} from "pianofunclub-crm/screens/schools/SchoolScreen";
import EnrolmentScreen, {
  screenOptions as enrolmentScreenOptions,
} from "pianofunclub-crm/screens/waitingList/EnrolmentScreen";

export type AccountsStackNavigatorParamList = {
  Account: AccountParams;
  AccountsHub?: {
    currentPageIndex?: number;
    discountType?: string;
    orderBy?: string;
    schoolId?: string;
    searchTerm?: string;
    status?: string;
  };
  DefaultTermDates: DefaultTermDatesParams;
  Enrolment: EnrolmentParams;
  Invoice: InvoiceParams;
  School: SchoolParams;
};

type NavigatorProps<T extends keyof AccountsStackNavigatorParamList> = StackNavigationProp<
  AccountsStackNavigatorParamList,
  T
>;

export type AccountsStackNavigatorProps<T extends keyof AccountsStackNavigatorParamList> =
  CompositeNavigationProp<NavigatorProps<T>, HubTabNavigatorProps<"AccountsHubTab">>;

export type AccountsStackRouteProps<T extends keyof AccountsStackNavigatorParamList> = RouteProp<
  AccountsStackNavigatorParamList,
  T
>;

const AccountsStackNavigator = createStackNavigator<AccountsStackNavigatorParamList>();

const AccountsNavigator = (): ReactElement => {
  return (
    <AccountsStackNavigator.Navigator screenOptions={defaultNavOptions}>
      <AccountsStackNavigator.Screen
        component={AccountsHubScreen}
        name="AccountsHub"
        options={accountsHubScreenOptions}
      />
      <AccountsStackNavigator.Screen
        component={AccountScreen}
        name="Account"
        options={accountScreenOptions}
      />
      <AccountsStackNavigator.Screen
        component={SchoolScreen}
        name="School"
        options={schoolScreenOptions}
      />
      <AccountsStackNavigator.Screen
        component={InvoiceScreen}
        name="Invoice"
        options={invoiceScreenOptions}
      />
      <AccountsStackNavigator.Screen
        component={DefaultTermDatesScreen}
        name="DefaultTermDates"
        options={defaultTermDatesScreenOptions}
      />
      <AccountsStackNavigator.Screen
        component={EnrolmentScreen}
        name="Enrolment"
        options={enrolmentScreenOptions}
      />
    </AccountsStackNavigator.Navigator>
  );
};

export default AccountsNavigator;
